// crashReporter — отправка JS-ошибок и worker-крашей в storys.
//
// Источники:
//   - window.onerror              → category='js', level='error'
//   - unhandledrejection          → category='js', level='error'
//   - WORKER_ERROR из worker'а    → category='worker', level='error'
//   - кнопка «Сообщить о проблеме» → category='manual', level='report'
//
// Дедуп: одинаковая (message, stack first line) в окне 30 секунд → count++,
// одна отправка вместо потока. Иначе при ошибке в onTick спам в БД.

import axios from 'axios';
import { STORYS_addres } from '../../API/API';
import { getCurrentUserId } from './cloudIO';
import useCadStore from './store/useCadStore';
import { SCHEMA_VERSION } from './documentIO';

const CLIENT_VERSION = 'cad-0.1.0';
const DEDUP_WINDOW_MS = 30 * 1000;

// Кэш дедупа: key=message+stack-first-line → {count, firstAt, lastAt}
const dedupCache = new Map();


function dedupKey(message, stack) {
    const stackTop = (stack || '').split('\n')[1] || '';
    return `${message}\n${stackTop}`.slice(0, 500);
}


/** Опубличить crash в storys (idempotent — глотает свои же ошибки). */
async function reportToServer(payload) {
    try {
        const userId = getCurrentUserId();
        const docState = useCadStore.getState();
        const body = {
            ...payload,
            user_id: userId || undefined,
            client_version: CLIENT_VERSION,
            schema_version: SCHEMA_VERSION,
            features_count: docState.doc?.features?.length || 0,
            bodies_count:   docState.bodyMeshes?.size || 0,
        };
        await axios.post(STORYS_addres + '/kubikon-cad/crash', body, { timeout: 5000 });
    } catch (err) {
        // Сами не валим. Логируем в console только в dev — в проде тихо.
        if (process.env.NODE_ENV !== 'production') {
            console.warn('[crashReporter] report failed:', err);
        }
    }
}


/** Главный публичный API — зарепортить ошибку. */
export function reportCrash({
    level = 'error',
    category = 'js',
    message,
    stack,
    userDescription,
}) {
    if (!message) return;
    const key = dedupKey(message, stack);
    const now = Date.now();
    const cached = dedupCache.get(key);
    if (cached && (now - cached.firstAt) < DEDUP_WINDOW_MS) {
        cached.count++;
        cached.lastAt = now;
        return;
    }
    // Чистим устаревшие записи (раз в N сообщений)
    if (dedupCache.size > 50) {
        for (const [k, v] of dedupCache.entries()) {
            if ((now - v.firstAt) > DEDUP_WINDOW_MS) dedupCache.delete(k);
        }
    }
    dedupCache.set(key, { count: 1, firstAt: now, lastAt: now });
    reportToServer({
        level, category,
        message: String(message).slice(0, 5000),
        stack: stack ? String(stack).slice(0, 10000) : undefined,
        count: 1,
        user_description: userDescription,
    });
}


/** Подключить глобальные обработчики window.onerror и unhandledrejection.
 *  Вызывается один раз при монтировании KubikonCAD. */
export function installGlobalCrashHandlers() {
    if (typeof window === 'undefined') return () => {};

    const onErr = (event) => {
        const err = event.error || event;
        const msg = err?.message || event.message || 'Unknown error';
        // Фильтруем «не наши» ошибки (другие части сайта).
        // В CAD-страницы стек обычно содержит 'KubikonCAD' или 'replicad' или 'geometry.worker'.
        const stack = err?.stack || '';
        if (!/KubikonCAD|geometry\.worker|replicad|sketch[A-Z]/i.test(stack + ' ' + msg)) {
            return; // не наша ошибка
        }
        reportCrash({
            level: 'error',
            category: 'js',
            message: msg,
            stack,
        });
    };

    const onRej = (event) => {
        const reason = event.reason;
        const msg = reason?.message || String(reason || 'Unknown rejection');
        const stack = reason?.stack || '';
        if (!/KubikonCAD|geometry\.worker|replicad|sketch[A-Z]/i.test(stack + ' ' + msg)) {
            return;
        }
        reportCrash({
            level: 'error',
            category: 'js',
            message: msg,
            stack,
        });
    };

    window.addEventListener('error', onErr);
    window.addEventListener('unhandledrejection', onRej);
    return () => {
        window.removeEventListener('error', onErr);
        window.removeEventListener('unhandledrejection', onRej);
    };
}


/** Manual-репорт от кнопки «Сообщить о проблеме».
 *  description — текст пользователя. */
export function reportManual(description) {
    return reportToServer({
        level: 'report',
        category: 'manual',
        message: description?.slice(0, 200) || '(пусто)',
        user_description: description,
        count: 1,
    });
}
